import { Tooltip } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Icon, notification } from 'ui';
import moment from 'utils/cops-moment';
import Table from '../../../common/table/table';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import {
  deleteAnnouncement,
  fetchAnnouncements,
} from '../../containers/announcements/announcements-actions';
import EditAnnouncementSidepanel from './edit-announcement-sidepanel';

const Container = styled.div`
  margin: 25px 15px;
`;

const AddButton = styled(Button)`
  margin: 20px 0;
  text-transform: uppercase;
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 20px;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : p.theme.colors.theme};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const Announcements = ({
  intl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isLoadingAnnouncements,
    announcementsError,
    announcements,
    isUpdatingAnnouncement,
    updateAnnouncementSuccess,
  } = useSelector(state => state.announcements);
  const [showConfirm] = useConfirmationMessage();

  useEffect(() => {
    dispatch(fetchAnnouncements());
  }, []);

  useEffect(() => {
    if (updateAnnouncementSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'message_center.SUCCESS' }),
        placement: 'top',
      });
    }
  }, [updateAnnouncementSuccess]);

  const columns = [
    {
      id: 'startDate',
      displayName: intl.formatMessage({ id: 'announcements.START_DATE' }),
      renderFn: (row) => {
        if (row.startDate) {
          return moment(row.startDate).format('DD/MM/YYYY HH:mm');
        } else if (row.isPublished) {
          return intl.formatMessage({ id: 'announcements.IMMEDIATE' });
        }
        return intl.formatMessage({ id: 'common.UNKNOWN' });
      },
      width: '200px',
    },
    {
      id: 'endDate',
      displayName: intl.formatMessage({ id: 'announcements.END_DATE' }),
      renderFn: (row) => {
        if (row.endDate) {
          return moment(row.endDate).format('DD/MM/YYYY HH:mm');
        } else if (row.isPublished) {
          return intl.formatMessage({ id: 'announcements.INDEFINITE' });
        }
        return intl.formatMessage({ id: 'common.UNKNOWN' });
      },
      width: '200px',
    },
    {
      id: 'announcementTitle',
      displayName: intl.formatMessage({ id: 'announcements.TITLE' }),
      fieldName: 'title',
      width: '400px',
      tooltip: row => row.title,
    },
    {
      id: 'type',
      displayName: intl.formatMessage({ id: 'announcements.TYPE' }),
      renderFn: row => row.category.description,
      width: '150px',
    },
    {
      id: 'scopes',
      displayName: intl.formatMessage({ id: 'announcements.SCOPE' }),
      renderFn: row => row.scopes?.map(s => s.name).join(', '),
      width: '300px',
    },
    {
      id: 'severity',
      displayName: intl.formatMessage({ id: 'announcements.SEVERITY' }),
      renderFn: row => row.severity.description,
      width: '150px',
    },
    {
      id: 'isPinned',
      displayName: intl.formatMessage({ id: 'announcements.PINNED' }),
      renderFn: row => row.isPinned && intl.formatMessage({ id: 'common.YES' }),
      width: '80px',
    },
    {
      id: 'hasBanner',
      displayName: intl.formatMessage({ id: 'announcements.BANNER' }),
      renderFn: row => row.hasBanner && intl.formatMessage({ id: 'common.YES' }),
      width: '80px',
    },
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      renderFn: row => <Icons>
        <Tooltip
          title={intl.formatMessage({ id: 'announcements.EDIT_ANNOUNCEMENT' })}
          key='edit'
          mouseEnterDelay={0.5}
          getPopupContainer={() => document.body}
        >
          <div>
            <StyledIcon
              type='pen'
              onClick={!isUpdatingAnnouncement ? () => navigate(`announcement/${row.id}`) : null}
              disabled={isUpdatingAnnouncement}
            />
          </div>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({ id: 'announcements.DELETE_ANNOUNCEMENT' })}
          key='delete'
          mouseEnterDelay={0.5}
          getPopupContainer={() => document.body}
        >
          <div>
            <StyledIcon
              type='trash-can'
              onClick={isUpdatingAnnouncement ?
                null :
                () => showConfirm(() => dispatch(deleteAnnouncement({ id: row.id })))}
              disabled={isUpdatingAnnouncement}
            />
          </div>
        </Tooltip>
      </Icons>,
    },
  ];

  return (
    <Container>
      <ErrorHandler type='notification' error={announcementsError} />
      <Table
        columns={columns}
        data={announcements || []}
        labelInColumn={true}
        isLoadingData={isLoadingAnnouncements}
      />
      <AddButton
        type='secondary'
        onClick={() => navigate('announcement')}
        disabled={isLoadingAnnouncements && !announcements?.length}
        data-test-id='btn-add-announcement'
      >
        {intl.formatMessage({ id: 'announcements.ADD_ANNOUNCEMENT' })}
      </AddButton>
      <Routes>
        <Route
          path={'announcement/:id?'}
          element={<EditAnnouncementSidepanel />}
        />
      </Routes>
    </Container>
  );
};

Announcements.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(Announcements);
